import { ChevronRight, CloudOff, RotateCw, Search } from "lucide-react";
import { GOAL_CATEGORY_LABELS } from "../lib/goalCategory";
import { normalizeGoalStatus } from "../lib/goalStatus";

/**
 * The Goals section's body on the new look (#1219): `PlanGoals.dc.html`
 * (round 10, approved by D243). One list in groups, short-term then
 * long-term, each goal one tappable card: title, category, "Due <date>" or
 * "No date", a bar and a percentage. A card carries no delete, slider or
 * status: those live in the opened goal (D282).
 *
 * One departure from the board, recorded in the PR: the board draws no
 * completed goals, so they sit in a third group, "Completed · N", at the
 * bottom, in the same card shape.
 *
 * `onOpenMilestones(goalId)` opens the goal (`?milestone=`). `groupRefs`
 * (`{ short, long }`) are the heading refs `?tab=short|long` scrolls to.
 */

const CHIP = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  fontSize: 11,
  fontWeight: 600,
  borderRadius: "var(--nw-radius-pill)",
  whiteSpace: "nowrap",
  color: "var(--nw-ink-lede)",
};

function GroupHeading({ groupRef, testId, children }) {
  return (
    <h2
      ref={groupRef}
      data-testid={testId}
      style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}
    >
      {children}
    </h2>
  );
}

function GoalCard({ g, onOpenMilestones }) {
  const title = g.target || g.goal_title;
  const category = GOAL_CATEGORY_LABELS[g.category] || GOAL_CATEGORY_LABELS.general;
  const pct = Math.max(0, Math.min(100, Number(g.progress_percentage) || 0));
  return (
    <div data-testid={`goal-${g.goal_id}`} style={{ minWidth: 0 }}>
      <button
        type="button"
        onClick={() => onOpenMilestones(g.goal_id)}
        data-testid={`goal-open-milestones-${g.goal_id}`}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
          minHeight: 44,
          padding: "12px 14px",
          textAlign: "left",
          font: "inherit",
          cursor: "pointer",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-card)",
          color: "var(--nw-ink)",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span
            style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--nw-ink)", minWidth: 0, overflowWrap: "anywhere" }}
          >
            {title}
          </span>
          <ChevronRight size={18} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
        </span>
        <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ ...CHIP, background: "var(--nw-line-faint)" }}>{category}</span>
          <span style={{ ...CHIP, background: "var(--nw-surface)", border: "1px solid var(--nw-line)" }}>
            {g.target_date ? `Due ${g.target_date}` : "No date"}
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label="Progress"
            style={{ flexGrow: 1, height: 6, borderRadius: 3, background: "var(--nw-line-faint)" }}
          >
            <span style={{ display: "block", width: `${pct}%`, height: 6, borderRadius: 3, background: "var(--nw-navy)" }} />
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--nw-ink)" }}>{pct}%</span>
        </span>
        {g.created_by_coach_id && (
          <span data-testid={`goal-assigned-by-${g.goal_id}`} style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>
            Assigned by {g.created_by_coach_name || "your coach"}
          </span>
        )}
      </button>
    </div>
  );
}

export default function PlanGoalsList({ goals, failed = false, onRetry, onOpenMilestones, groupRefs = {} }) {
  if (failed) {
    return (
      <div
        data-testid="goals-error"
        style={{ display: "flex", flexDirection: "column", gap: 8, padding: 16, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CloudOff size={20} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
          <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>Couldn't load your development plan</div>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Nothing has been lost — we just couldn't reach the server.</div>
        <button
          type="button"
          onClick={onRetry}
          data-testid="goals-error-retry"
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            minHeight: 44,
            padding: "0 14px",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line-strong)",
            borderRadius: "var(--nw-radius-control)",
            fontSize: 14,
            fontWeight: 600,
            color: "var(--nw-navy)",
            cursor: "pointer",
          }}
        >
          <RotateCw size={14} strokeWidth={2} aria-hidden="true" />
          Try again
        </button>
      </div>
    );
  }

  if (goals.length === 0) {
    return (
      <div
        data-testid="goals-empty"
        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 20px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)", textAlign: "center" }}
      >
        <div style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nw-navy-tint)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Search size={22} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" />
        </div>
        <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>No goals yet.</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>
          Some ideas: improve your batting average, take more catches, lower your bowling economy.
        </div>
      </div>
    );
  }

  const open = (g) => normalizeGoalStatus(g.status) !== "completed";
  const short = goals.filter((g) => open(g) && (g.horizon || "short") === "short");
  const long = goals.filter((g) => open(g) && g.horizon === "long");
  const done = goals.filter((g) => !open(g));
  const groups = [
    { key: "short", label: "Short-term", rows: short, ref: groupRefs.short },
    { key: "long", label: "Long-term", rows: long, ref: groupRefs.long },
    { key: "completed", label: "Completed", rows: done, ref: undefined },
  ].filter((x) => x.rows.length > 0);

  // One column, 8px between every card and heading (the board's rhythm).
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {groups.map((x) => (
        <section key={x.key} data-testid={`goals-group-${x.key}`} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <GroupHeading groupRef={x.ref} testId={`plan-goals-group-${x.key}`}>
            {x.label} · {x.rows.length}
          </GroupHeading>
          {x.rows.map((g) => (
            <GoalCard key={g.goal_id} g={g} onOpenMilestones={onOpenMilestones} />
          ))}
        </section>
      ))}
    </div>
  );
}
